import { computed, type MaybeRefOrGetter } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { userQueries } from '@services/identity-api/queries/User';
import { type IdentityWebModelsUserUserClaimDto } from '@services/identity-api/api';
import { type FlattenedPermissions } from '@permission/types';

function hasPermission(
  claims: IdentityWebModelsUserUserClaimDto[],
  permission: FlattenedPermissions,
): boolean {
  return claims.some((t) => t.type === 'Permission' && t.value === permission);
}

function buildClaimsObject(
  claims: IdentityWebModelsUserUserClaimDto[] | undefined,
  isLoading: boolean,
  isSuccess: boolean,
) {
  const checkClaim = (type: string) =>
    claims?.some((d) => d.type === type && d.value === 'True') ?? false;

  return {
    isInitialLoading: isLoading,
    magBeheerboxInzien: hasPermission(claims ?? [], 'Inregeling.InregelingInzien'),
    magAdviseurbeheerInzien: hasPermission(claims ?? [], 'Crm.Adviseurbeheer'),
    isSuccess,
    isWebmodule: claims?.some((d) => d.type === 'WebmoduleId' && d.value !== '0') ?? false,
    isTemporaryUser: checkClaim('IsTemporaryUser'),
    isInternal: checkClaim('IsInternal'),
    isFinancieel: checkClaim('HeeftFinancieelPermissie'),
    heeftInstellingenPermissie: checkClaim('HeeftInstellingenPermissie'),
    isMaatschappij: checkClaim('IsMaatschappij'),
    isAdviseur: checkClaim('IsAdviseur'),
    webmoduleId: claims?.some((d) => d.type === 'WebmoduleId')
      ? Number(claims?.filter((d) => d.type === 'WebmoduleId')[0].value)
      : undefined,
  };
}

export function useClaims(enabled: MaybeRefOrGetter<boolean | undefined> = undefined) {
  const {
    data: claims,
    isLoading,
    isSuccess,
  } = useQuery({
    ...userQueries.getClaims,
    staleTime: 60 * 1000,
    retry: 10,
    enabled,
  });

  return computed(() => buildClaimsObject(claims.value?.data, isLoading.value, isSuccess.value));
}

export async function useClaimsAwaitable() {
  const client = useQueryClient();
  const claims = await client.ensureQueryData({
    ...userQueries.getClaims,
    staleTime: 60 * 1000,
    retry: 10,
  });
  return buildClaimsObject(claims.data, false, true);
}

export function useRefreshClaims(): { refresh: () => Promise<void> } {
  const queryClient = useQueryClient();
  return { refresh: () => queryClient.invalidateQueries(userQueries.getClaims) };
}
